// src/pages/TeamMessagesFullPage.tsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast as sonnerToast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useRole } from '@/data/RoleContext';
import { useTeamMessages } from '@/hooks/useTeamMessages';
import { KoreadorIntro } from '@/components/team-messages/KoreadorIntro';
import { FullPageRail, type RailSection } from '@/components/team-messages/FullPageRail';
import { FullPageConvList } from '@/components/team-messages/FullPageConvList';
import { FullPageThread } from '@/components/team-messages/FullPageThread';
import type { TeamConversation, OrgMember } from '@/types/teamMessages';

// Roles that may create groups / add members
const GROUP_ADMIN_ROLES = ['super_admin', 'admin', 'owner', 'ops_manager'];
const ADD_MEMBERS_ROLES = ['super_admin', 'admin', 'owner'];

// ─── NewGroupDialog ───────────────────────────────────────────────────────────

interface NewGroupDialogProps {
  open: boolean;
  onClose: () => void;
  orgMembers: OrgMember[];
  onCreateGroup: (name: string, memberIds: string[]) => Promise<void>;
}

function NewGroupDialog({ open, onClose, orgMembers, onCreateGroup }: NewGroupDialogProps) {
  const [groupName, setGroupName] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);

  const toggle = (id: string) =>
    setSelectedIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const handleCreate = async () => {
    if (!groupName.trim()) return;
    setCreating(true);
    try {
      await onCreateGroup(groupName.trim(), selectedIds);
      setGroupName(''); setSelectedIds([]); onClose();
      sonnerToast.success('Group created');
    } catch {
      sonnerToast.error('Failed to create group');
    } finally {
      setCreating(false);
    }
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) { setGroupName(''); setSelectedIds([]); onClose(); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">Create Group</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
              Group Name
            </label>
            <Input
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="e.g. ordering, qa-team"
              className="h-8 text-xs"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
              Add Members
            </label>
            <div className="rounded-md border border-border divide-y divide-border/60 overflow-hidden max-h-48 overflow-y-auto">
              {orgMembers.map((m) => (
                <label
                  key={m.userId}
                  className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-muted/30 transition-colors"
                >
                  <Checkbox
                    checked={selectedIds.includes(m.userId)}
                    onCheckedChange={() => toggle(m.userId)}
                    className="shrink-0"
                  />
                  <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <span className="text-[9px] font-semibold text-muted-foreground">{m.initials}</span>
                  </div>
                  <span className="text-xs font-medium flex-1">{m.displayName}</span>
                  {m.role && (
                    <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 font-normal">
                      {m.role}
                    </Badge>
                  )}
                </label>
              ))}
              {orgMembers.length === 0 && (
                <p className="px-3 py-2 text-[10px] text-muted-foreground/50">No members found</p>
              )}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" size="sm" className="text-xs h-8" onClick={onClose} disabled={creating}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="text-xs h-8"
            disabled={!groupName.trim() || creating}
            onClick={() => void handleCreate()}
          >
            {creating && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Create Group
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── AddMembersDialog ─────────────────────────────────────────────────────────

interface AddMembersDialogProps {
  open: boolean;
  onClose: () => void;
  conversation: TeamConversation;
  orgMembers: OrgMember[];
  onAddMembers: (conversationId: string, memberIds: string[]) => Promise<void>;
}

function AddMembersDialog({
  open,
  onClose,
  conversation,
  orgMembers,
  onAddMembers,
}: AddMembersDialogProps) {
  const [search, setSearch]           = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [saving, setSaving]           = useState(false);

  const currentIds = new Set(conversation.members.map((m) => m.userId));
  const available  = orgMembers.filter(
    (m) =>
      !currentIds.has(m.userId) &&
      m.displayName.toLowerCase().includes(search.toLowerCase()),
  );

  const toggle = (id: string) =>
    setSelectedIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const handleSubmit = async () => {
    if (!selectedIds.length) return;
    setSaving(true);
    try {
      await onAddMembers(conversation.id, selectedIds);
      setSelectedIds([]); setSearch(''); onClose();
      sonnerToast.success(
        `${selectedIds.length} member${selectedIds.length > 1 ? 's' : ''} added`,
      );
    } catch {
      sonnerToast.error('Failed to add members');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) { setSelectedIds([]); setSearch(''); onClose(); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            Members — {conversation.name ?? 'Group'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
              Current Members
            </label>
            <div className="rounded-md border border-border divide-y divide-border/60 max-h-36 overflow-y-auto">
              {conversation.members.map((m) => (
                <div key={m.userId} className="flex items-center gap-2.5 px-3 py-2">
                  <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <span className="text-[9px] font-semibold text-muted-foreground">{m.initials}</span>
                  </div>
                  <span className="text-xs font-medium flex-1">{m.displayName}</span>
                  {m.role && (
                    <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 font-normal">
                      {m.role}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
              Add Members
            </label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name…"
              className="h-8 text-xs"
            />
            <div className="rounded-md border border-border divide-y divide-border/60 max-h-40 overflow-y-auto">
              {available.map((m) => (
                <label
                  key={m.userId}
                  className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-muted/30 transition-colors"
                >
                  <Checkbox
                    checked={selectedIds.includes(m.userId)}
                    onCheckedChange={() => toggle(m.userId)}
                    className="shrink-0"
                  />
                  <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <span className="text-[9px] font-semibold text-muted-foreground">{m.initials}</span>
                  </div>
                  <span className="text-xs font-medium flex-1">{m.displayName}</span>
                  {m.role && (
                    <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 font-normal">
                      {m.role}
                    </Badge>
                  )}
                </label>
              ))}
              {available.length === 0 && (
                <p className="px-3 py-2 text-[10px] text-muted-foreground/50">
                  {search
                    ? 'No members match your search'
                    : 'All org members already in this group'}
                </p>
              )}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-8"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="text-xs h-8"
            disabled={!selectedIds.length || saving}
            onClick={() => void handleSubmit()}
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Add Members
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Root page ────────────────────────────────────────────────────────────────

export default function TeamMessagesFullPage() {
  const navigate = useNavigate();
  const { role } = useRole();

  const {
    conversations,
    activeConversationId,
    messages,
    orgMembers,
    loadingConversations,
    loadingMessages,
    updatesChannelId,
    currentUserId,
    mentionCount,
    selectConversation,
    handleSend,
    handleCreateGroup,
    handleAddMembers,
    handleStartDM,
  } = useTeamMessages();

  const [introComplete,        setIntroComplete]        = useState(false);
  const [activeSection,        setActiveSection]        = useState<RailSection>('channels');
  const [draftByConv,          setDraftByConv]          = useState<Record<string, string>>({});
  const [showNewGroupDialog,   setShowNewGroupDialog]   = useState(false);
  const [showAddMembersDialog, setShowAddMembersDialog] = useState(false);

  const searchRef = useRef<HTMLInputElement>(null);

  const canCreateGroup = GROUP_ADMIN_ROLES.includes(role);
  const canAddMembers  = ADD_MEMBERS_ROLES.includes(role);

  const activeConversation =
    conversations.find((c) => c.id === activeConversationId) ?? null;

  const handleSelectConversation = useCallback(
    (id: string) => {
      selectConversation(id);
    },
    [selectConversation],
  );

  // Explicit section switch after creating a group or starting a DM — more
  // reliable than the useEffect which can race if conversations state lags.
  const handleCreateGroupAndSwitch = useCallback(
    async (name: string, memberIds: string[]) => {
      await handleCreateGroup(name, memberIds);
      setActiveSection('groups');
    },
    [handleCreateGroup],
  );

  const handleStartDMAndSwitch = useCallback(
    async (userId: string) => {
      await handleStartDM(userId);
      setActiveSection('dms');
    },
    [handleStartDM],
  );

  // Auto-switch rail section whenever the active conversation changes (covers
  // both user clicks and programmatic activation from group/DM creation).
  useEffect(() => {
    if (!activeConversationId) return;
    const conv = conversations.find((c) => c.id === activeConversationId);
    if (!conv) return;
    if (conv.type === 'dm') setActiveSection('dms');
    else if (conv.type === 'group') setActiveSection('groups');
    else setActiveSection('channels');
  }, [activeConversationId, conversations]);

  const draft = activeConversationId ? (draftByConv[activeConversationId] ?? '') : '';

  const handleDraftChange = (v: string) => {
    if (!activeConversationId) return;
    setDraftByConv((prev) => ({ ...prev, [activeConversationId]: v }));
  };

  const onSend = async (
    body: string,
    attachments: { name: string; isImage: boolean; previewUrl: string | null }[],
    mentionedUserIds: string[] = [],
  ) => {
    if (!body.trim() && attachments.length === 0) return;
    await handleSend(body, attachments, mentionedUserIds);
    if (activeConversationId) {
      setDraftByConv((prev) => ({ ...prev, [activeConversationId]: '' }));
    }
  };

  const userInitials =
    orgMembers.find((m) => m.userId === currentUserId)?.initials ?? '?';

  return (
    <div className="h-screen flex overflow-hidden" style={{ background: 'hsl(230 15% 10%)' }}>
      {/* Intro animation — renders on top until dismissed */}
      {!introComplete && <KoreadorIntro onDone={() => setIntroComplete(true)} />}

      {/* Rail */}
      <FullPageRail
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        onBack={() => navigate(-1)}
        userInitials={userInitials}
        mentionCount={mentionCount}
        onSearchFocus={() => searchRef.current?.focus()}
        onNewGroup={() => setShowNewGroupDialog(true)}
      />

      {/* Conversation list */}
      <FullPageConvList
        section={activeSection}
        conversations={conversations}
        orgMembers={orgMembers}
        currentUserId={currentUserId}
        activeConversationId={activeConversationId}
        updatesChannelId={updatesChannelId}
        loadingConversations={loadingConversations}
        canCreate={canCreateGroup}
        onSelectConversation={handleSelectConversation}
        onStartDM={handleStartDMAndSwitch}
        onNewGroup={() => setShowNewGroupDialog(true)}
        searchRef={searchRef}
      />

      {/* Thread */}
      <FullPageThread
        conversation={activeConversation}
        messages={messages}
        loadingMessages={loadingMessages}
        updatesChannelId={updatesChannelId}
        currentUserId={currentUserId}
        orgMembers={orgMembers}
        canAddMembers={activeConversationId !== updatesChannelId && canAddMembers}
        draft={draft}
        onDraftChange={handleDraftChange}
        onSend={onSend}
        onAddMembers={() => setShowAddMembersDialog(true)}
      />

      {/* Dialogs */}
      <NewGroupDialog
        open={showNewGroupDialog}
        onClose={() => setShowNewGroupDialog(false)}
        orgMembers={orgMembers}
        onCreateGroup={handleCreateGroupAndSwitch}
      />
      {activeConversation?.type === 'group' && (
        <AddMembersDialog
          open={showAddMembersDialog}
          onClose={() => setShowAddMembersDialog(false)}
          conversation={activeConversation}
          orgMembers={orgMembers}
          onAddMembers={handleAddMembers}
        />
      )}
    </div>
  );
}
